import { useEffect, useMemo, useRef, useState } from "react";
import { Upload } from "lucide-react";
import { fontCssFamily, type Project } from "@proof/core";
import { loadBrowserFonts } from "@proof/fonts/browser";
import { Button, Control } from "./components";
import {
  bindFontLibraryServices,
  createFontLibraryStyle,
  snapshotFontLibraryRecords,
  downloadFontLibraryFace,
  type FontLibraryRecord,
  type FontLibraryServices,
  type FontLibraryArtifact,
} from "./font-library";

export interface FontLibraryProps {
  project: Project;
  services: FontLibraryServices;
  onChange(project: Project): void;
  notify?: (message: string) => void;
  canApply?: boolean;
  onDownload?: (
    artifact: FontLibraryArtifact,
    signal: AbortSignal,
  ) => void | Promise<void>;
}
function FontSample({
  record,
  services,
  signal,
}: {
  record: FontLibraryRecord;
  services: FontLibraryServices;
  signal: AbortSignal;
}) {
  const [state, setState] = useState("loading"),
    face = record.face;
  useEffect(() => {
    const controller = new AbortController(),
      joined = AbortSignal.any([signal, controller.signal]);
    let dispose: (() => void) | undefined;
    setState("loading");
    void loadBrowserFonts(
      [face],
      (face, signal) => services.loadFont(face, signal!),
      joined,
    )
      .then((fonts) => {
        if (joined.aborted) fonts.dispose();
        else {
          dispose = fonts.dispose;
          setState("ready");
        }
      })
      .catch(() => {
        if (!joined.aborted) setState("Managed font preview unavailable");
      });
    return () => {
      controller.abort();
      dispose?.();
    };
  }, [face, services, signal]);
  return (
    <div className="font-sample">
      {state === "ready" ? (
        <strong
          style={{
            fontFamily: fontCssFamily(face),
            fontWeight: face.weight,
            fontStyle: face.style,
            fontSynthesis: "none",
          }}
        >
          Aa 123 — {face.family}
        </strong>
      ) : (
        <span role={state === "loading" ? "status" : "alert"}>
          {state === "loading" ? "Loading font preview…" : state}
        </span>
      )}
    </div>
  );
}
export function FontLibrary(props: FontLibraryProps) {
  const {
    project,
    canApply = true,
    onDownload = downloadFontLibraryFace,
  } = props;
  const setup = useMemo(() => {
    try {
      return { services: bindFontLibraryServices(props.services) };
    } catch {
      return { error: "Provide font catalog and managed-byte services." };
    }
  }, [props.services, project, onDownload]);
  const services = setup.services,
    scope = useRef<AbortController>(undefined);
  const [catalog, setCatalog] = useState<{
    setup: typeof setup;
    records: FontLibraryRecord[];
  }>();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [showArchived, setArchived] = useState(false),
    [search, setSearch] = useState(""),
    [page, setPage] = useState(0);
  const operation = useRef<AbortController>(undefined),
    latest = useRef(setup);
  useEffect(() => {
    setSearch("");
    setArchived(false);
  }, [project.id, project.tenantId]);
  latest.current = setup;
  const records = catalog?.setup === setup ? catalog.records : [];
  const current = (signal: AbortSignal) =>
    latest.current === setup && !signal.aborted;
  const announce = (text: string) => {
    setMessage(text);
    props.notify?.(text);
  };
  async function run(
    label: string,
    action: (signal: AbortSignal) => Promise<string | void>,
  ) {
    if (!services || !scope.current || scope.current.signal.aborted) return;
    operation.current?.abort();
    const controller = new AbortController();
    operation.current = controller;
    const signal = AbortSignal.any([scope.current.signal, controller.signal]);
    setBusy(label);
    setError("");
    setMessage("");
    try {
      const text = await action(signal);
      if (current(signal) && text) announce(text);
    } catch {
      if (current(signal))
        setError("Font operation failed. Check the font services and retry.");
    } finally {
      if (current(signal) && operation.current === controller) setBusy("");
    }
  }
  const refresh = async (signal: AbortSignal) => {
    const result = await services!.listFonts(signal);
    if (!current(signal)) return;
    const next = snapshotFontLibraryRecords(result, project.tenantId);
    setCatalog({ setup, records: next });
    setPage(0);
  };
  useEffect(() => {
    const controller = new AbortController();
    scope.current = controller;
    setCatalog(undefined);
    setPage(0);
    setError("");
    setMessage("");
    setBusy("");
    void run("Loading fonts…", refresh);
    return () => {
      controller.abort();
      operation.current?.abort();
    };
  }, [setup]);
  const filtered = records.filter(
    (r) =>
      (showArchived || r.status === "active") &&
      `${r.face.name} ${r.face.family}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const lastPage = Math.max(0, Math.ceil(filtered.length / 12) - 1),
    visiblePage = Math.min(page, lastPage);
  return (
    <section
      className="panel padded managed-font-library"
      aria-label="Managed font library"
    >
      <div className="section-title">
        <h3>Font library</h3>
        {services?.uploadFont && (
          <label className="button">
            <Upload size={14} />
            Upload font
            <input
              className="sr-only"
              aria-label="Font file"
              disabled={!!busy}
              type="file"
              accept=".ttf,.otf,.woff,.woff2"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (file)
                  void run("Validating font…", async (signal) => {
                    const result = await services.uploadFont!(file, signal);
                    if (!current(signal)) return;
                    const record = snapshotFontLibraryRecords(
                      [result],
                      project.tenantId,
                    )[0];
                    await refresh(signal);
                    if (!current(signal)) return;
                    setSearch(record.face.name);
                    return `Uploaded ${record.face.name}.`;
                  });
              }}
            />
          </label>
        )}
      </div>
      <p className="muted">
        Static TTF, OTF, WOFF, and WOFF2 · up to 8 MB per upload. Exact managed
        faces are shared with live proof and embedded in PDF. Bold and italic
        text need matching faces.
      </p>
      {setup.error && <p role="alert">{setup.error}</p>}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      {busy && (
        <div className="button-row">
          <p role="status">{busy}</p>
          <Button
            onClick={() => {
              operation.current?.abort();
              setBusy("");
              setMessage("Font operation cancelled.");
            }}
          >
            Cancel font operation
          </Button>
        </div>
      )}
      <div className="button-row">
        <Button
          disabled={!!busy || !services}
          onClick={() => void run("Refreshing fonts…", refresh)}
        >
          Refresh fonts
        </Button>
      </div>
      <div className="control-grid">
        <Control label="Search fonts">
          <input
            type="search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
        </Control>
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => {
              setArchived(e.target.checked);
              setPage(0);
            }}
          />
          Show archived fonts
        </label>
        <p>
          {records.filter((r) => r.status === "active").length} stored active
          faces. Font bytes come from managed storage, independent of installed
          fonts on the viewer's computer.
        </p>
      </div>
      <div className="button-row">
        <Button
          disabled={visiblePage === 0}
          onClick={() => setPage(visiblePage - 1)}
        >
          Previous fonts
        </Button>
        <span>
          Page {visiblePage + 1} of {lastPage + 1} · {filtered.length} faces
        </span>
        <Button
          disabled={visiblePage === lastPage}
          onClick={() => setPage(visiblePage + 1)}
        >
          Next fonts
        </Button>
      </div>
      {!busy && catalog?.setup === setup && !filtered.length && (
        <p role="status">No fonts match this view.</p>
      )}
      <div className="managed-font-grid">
        {services &&
          scope.current &&
          filtered
            .slice(visiblePage * 12, visiblePage * 12 + 12)
            .map((record) => (
              <article
                className="managed-font-card"
                key={record.face.id}
                data-font-id={record.face.id}
              >
                <FontSample
                  record={record}
                  services={services}
                  signal={scope.current!.signal}
                />
                <strong>{record.face.name}</strong>
                <small>
                  {record.origin === "installed-baseline"
                    ? "Stored baseline face"
                    : record.system
                      ? "Bundled system face"
                      : "Tenant upload"}{" "}
                  · {record.face.weight} {record.face.style} ·{" "}
                  {record.face.embedding} embedding
                  {record.status === "archived" ? " · Archived" : ""}
                </small>
                <div className="button-row">
                  <Button
                    disabled={
                      !!busy || record.status === "archived" || !canApply
                    }
                    onClick={() => {
                      try {
                        props.onChange(
                          createFontLibraryStyle(project, record, records),
                        );
                        announce(
                          "Created a reusable text style with immutable font snapshots.",
                        );
                      } catch {
                        setError(
                          "Could not create the font style. Refresh the catalog and retry.",
                        );
                      }
                    }}
                  >
                    Create text style
                  </Button>
                  <Button
                    disabled={!!busy}
                    onClick={() =>
                      void run("Preparing font download…", async (signal) => {
                        const bytes = await services.loadFont(
                          record.face,
                          signal,
                        );
                        if (!current(signal)) return;
                        await onDownload(
                          {
                            blob: new Blob(
                              [bytes.slice().buffer as ArrayBuffer],
                              { type: record.face.mime },
                            ),
                            filename: record.face.objectKey,
                            record: structuredClone(record),
                          },
                          signal,
                        );
                        if (current(signal)) return "Font download prepared.";
                      })
                    }
                  >
                    Download face
                  </Button>
                  {services.archiveFont &&
                    !record.system &&
                    record.status === "active" && (
                      <Button
                        disabled={!!busy}
                        onClick={() =>
                          void run("Archiving font…", async (signal) => {
                            const result = await services.archiveFont!(
                              record.face.id,
                              signal,
                            );
                            if (!current(signal)) return;
                            const archived = snapshotFontLibraryRecords(
                              [result],
                              project.tenantId,
                            )[0];
                            if (
                              archived.system ||
                              archived.status !== "archived" ||
                              archived.face.id !== record.face.id
                            )
                              throw new Error("Invalid archive acknowledgment");
                            await refresh(signal);
                            return `Archived ${record.face.name}. Existing document snapshots are retained.`;
                          })
                        }
                      >
                        Archive
                      </Button>
                    )}
                </div>
              </article>
            ))}
      </div>
    </section>
  );
}
